import { useMediaQuery } from "@mui/material";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type SidebarContextValue = {
  sidebarCollapsed: boolean;
  isMobile: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
};

export const SidebarContext = createContext<SidebarContextValue>({
  sidebarCollapsed: false,
  isMobile: false,
  toggleSidebar: () => {},
  setSidebarCollapsed: () => {},
});

export function SidebarProvider({ children }: { children: ReactNode }) {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Collapse sidebar by default on mobile; expand on desktop
  useEffect(() => {
    setSidebarCollapsed(isMobile);
  }, [isMobile]);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({
      sidebarCollapsed,
      isMobile,
      toggleSidebar,
      setSidebarCollapsed,
    }),
    [sidebarCollapsed, isMobile, toggleSidebar]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSidebar() {
  return useContext(SidebarContext);
}
